/**
 * FlowPlannerModel.js
 *
 * Central data model for the Flow Planner.
 * initializeModel() — input transformation, default overrides, and output extraction.
 * Outputs (gridData, laborSummary) are read directly from backend output.shiftPlan[].
 *
 * Model shape:
 *   inputs    — Transformed data from API / ops clock (read-only after init)
 *   overrides — User-editable inputs with sensible defaults
 *   outputs   — { gridData, laborSummary, laborAvailabilityChart }
 */

import { getAvailableCycles } from '../../utils/helpers';
import { getCycleEvents } from './VolumeDistributionCalculator';
import { getProcessesByProcessingType, isIndirectProcess, PROCESSING_TYPE } from '../../utils/adminSettingsUtil';

import {
  FLOW_PROCESSES,
  DEFAULT_MULTIPLIER,
  DISPLAY_OVERRIDES,
  GROUP_SKELETON,
  VOLUME_SOURCE_OVERRIDES,
  DYNAMIC_V2_KEY_OVERRIDES
} from './FlowPlannerGridConfig';

import {
  buildLaborAvailabilityChartData,
  computeAvailableHCPerInterval,
  computeOpportunityHCPerInterval
} from './LaborAvailabilityChartModel';
import { PLAN_VERSIONS, DEFAULT_ATT_PCT } from '../../utils/constants';

function extractCycleVolumes(packageProjectionList) {
  if (!packageProjectionList) return [];
  return packageProjectionList.map((p) => ({
    name: p.applicability.identifier,
    plannedVolume: p.volume.plannedVolume || 0,
    sidelineRTS: p.volume.lastDayRolloverVolume || 0,
    plannedRollover: p.volume.plannedRolloverVolume || 0
  }));
}

function extractProcessRates(planData, cycleName) {
  const cycleData = planData?.input?.resourceProductivityInput?.cycleResourceProductivities?.[cycleName];
  if (!cycleData?.pph) return [];
  return Object.entries(cycleData.pph)
    .filter(([, rates]) => rates.targetPph > 0)
    .map(([rawName, rates]) => ({
      name: DISPLAY_OVERRIDES[rawName] || rawName,
      targetRate: Math.round(rates.targetPph)
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Extract per-interval multipliers and planned rates from cycleTimeWindowResourceProductivities.
 * Returns { processName: { multipliers: number[], rates: number[] } } or null if unavailable.
 */
function extractTimeWindowRates(planData, cycleName, numIntervals) {
  const timeWindows = planData?.input?.resourceProductivityInput?.cycleTimeWindowResourceProductivities?.[cycleName];
  if (!Array.isArray(timeWindows) || timeWindows.length === 0) return null;

  const result = {};
  const windowCount = Math.min(timeWindows.length, numIntervals);

  timeWindows.slice(0, windowCount).forEach((tw, idx) => {
    if (!tw?.pph) return;
    Object.entries(tw.pph).forEach(([rawName, data]) => {
      const displayName = DISPLAY_OVERRIDES[rawName] || rawName;
      if (!result[displayName]) {
        result[displayName] = {
          multipliers: Array(numIntervals).fill(DEFAULT_MULTIPLIER),
          rates: Array(numIntervals).fill(0)
        };
      }
      result[displayName].multipliers[idx] = data.pphMultiplier ?? DEFAULT_MULTIPLIER;
      result[displayName].rates[idx] = Math.round(data.plannedPph ?? 0);
    });
  });

  return Object.keys(result).length > 0 ? result : null;
}

// ─── DST-safe ISO timestamp helpers ─────────────────────────────────────────
// Backend sends local station time strings like "2026-04-13T01:20:00".
// Parsed as UTC (append 'Z') so arithmetic is correct regardless of browser TZ.

function parseLocalISO(isoStr) {
  if (!isoStr) return 0;
  const s = String(isoStr);
  return Math.floor(new Date(s.endsWith('Z') ? s : s + 'Z').getTime() / 1000);
}

export function formatEpochAsHHMM(epochSec) {
  if (!epochSec || !isFinite(epochSec)) return '--:--';
  const d = new Date(epochSec * 1000);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

// ─── Ops clock end time (station local ISO string) ──────────────────────────
// Returns the latest endTime across all ops clock events as a raw ISO string.
function getOpsClockEndTime(planData) {
  const opsClockMap = planData?.input?.opsclockInput?.opsClockMap;
  if (!opsClockMap) return null;
  let maxEnd = null;
  Object.values(opsClockMap).forEach((events) => {
    (events || []).forEach((event) => {
      const end = event.endTime;
      if (end && (!maxEnd || end > maxEnd)) maxEnd = end;
    });
  });
  return maxEnd;
}

// ─── Backend ops clock adapter (for processEvents / cycle sorting / breaks) ─

function adaptOpsClockInput(planData) {
  const opsInput = planData?.input?.opsclockInput;
  if (!opsInput?.opsClockMap) return null;

  const clockEvents = [];
  Object.entries(opsInput.opsClockMap).forEach(([cycleName, events]) => {
    (events || []).forEach((event) => {
      clockEvents.push({
        process: (event.process || '').toUpperCase(),
        cycleName,
        timeIntervals: [
          {
            startTime: parseLocalISO(event.startTime),
            endTime: parseLocalISO(event.endTime)
          }
        ]
      });
    });
  });

  const breaks = (opsInput.breaks || []).map((b) => ({
    breakName: b.breakName,
    breakType: b.breakType,
    startTime: parseLocalISO(b.timeWindow?.start),
    endTime: parseLocalISO(b.timeWindow?.end)
  }));

  return { clockEvents, breaks };
}

// ─── Extract intervals from backend shiftPlan output ────────────────────────
// Each shiftPlan entry has timeWindow { start, end }.
// Breaks from ops clock are attached to intervals they overlap.

function extractIntervalsFromShiftPlan(planData, breaks) {
  const shiftPlan = planData?.output?.shiftPlan;
  if (!Array.isArray(shiftPlan) || shiftPlan.length === 0) return [];

  const cycleMap = groupShiftPlanByCycle(shiftPlan);
  const firstCycle = Object.keys(cycleMap)[0];
  const entries = cycleMap[firstCycle] || [];

  return entries.map((entry, idx) => {
    const startTime = parseLocalISO(entry.timeWindow?.start);
    const endTime = parseLocalISO(entry.timeWindow?.end);
    const startLabel = formatEpochAsHHMM(startTime);
    const endLabel = formatEpochAsHHMM(endTime);
    const overlapping = (breaks || []).filter((b) => b.startTime < endTime && b.endTime > startTime);
    return {
      name: `P${idx + 1}`,
      startTime,
      endTime,
      startLabel,
      endLabel,
      breaks: overlapping,
      displayLabel: `P${idx + 1}\n${startLabel}-${endLabel}`
    };
  });
}

// ─── Labor opportunities extractor ──────────────────────────────────────────
// StaticV3ModelExecutor runs the LaborOpportunityRecommendationProviderV2
// against the plan and emits the result on the GetShiftPlan response under
// output.labourOpportunities.
//
// Each entry shape:
//   { opportunityType, headcount, workgroupTags,
//     applicability: { timeWindow: { start, end } },
//     signUpWindow:  { start, end } }

function extractLaborOpportunities(planData) {
  const opportunities = planData?.output?.labourOpportunities;
  return Array.isArray(opportunities) ? opportunities : [];
}

// ─── Backend output volume distribution extractor ───────────────────────────
// Reads planData.output.volumeDistribution → per-cycle per-process { percentages, volumes }
// Backend shape: volumeDistribution[cycle].volumeDistribution[process].intervals[]
// Each process entry has a processGroup field (Induct, Stow, Pick).

function extractOutputVolumeDistribution(planData) {
  const volDist = planData?.output?.volumeDistribution;
  if (!Array.isArray(volDist)) return null;

  const result = {};
  volDist.forEach((cycleEntry) => {
    const cycleName = cycleEntry?.applicability?.identifier;
    if (!cycleName) return;

    const processDists = cycleEntry?.volumeDistribution;
    if (!Array.isArray(processDists) || processDists.length === 0) return;

    result[cycleName] = {};
    processDists.forEach((pd) => {
      const processKey = (pd.processGroup || '').toUpperCase();
      const intervals = pd?.intervals;
      if (!processKey || !Array.isArray(intervals) || intervals.length === 0) return;
      result[cycleName][processKey] = {
        percentages: intervals.map((i) => i.distributionPercentage || 0),
        volumes: intervals.map((i) => i.volume || 0)
      };
    });
  });

  return Object.keys(result).length > 0 ? result : null;
}

const FLOW_NAME_MAP = { Induct: 'induct', Stow: 'stow', Divert: 'diverter' };
const REVERSE_FLOW_NAME_MAP = Object.fromEntries(Object.entries(FLOW_NAME_MAP).map(([k, v]) => [v, k]));
const REVERSE_DISPLAY_OVERRIDES = Object.fromEntries(Object.entries(DISPLAY_OVERRIDES).map(([k, v]) => [v, k]));

/**
 * Maps a single ExecutionPlanBean's flows[] to the frontend automation-config shape.
 * Backend shape:  flows[{ name, executionModes[] }]  (from executionPlan or executionPlanMap[cycle])
 * Frontend shape: { induct: [...], stow: [...], diverter: [...] }
 */
function flowsToAutomationConfig(flows) {
  if (!Array.isArray(flows) || flows.length === 0) return null;

  const config = {};
  flows.forEach((flow) => {
    const key = FLOW_NAME_MAP[flow.name];
    if (!key || !Array.isArray(flow.executionModes)) return;

    config[key] = flow.executionModes.map((mode) => ({
      executionType: mode.executionType,
      isEnabled: mode.isEnabled ?? true,
      volumeSplitPercentage: mode.volumeSplitPercentage ?? null,
      lineCount: mode.lineCount ?? null,
      priority: mode.priority ?? null,
      processName: mode.processName ?? null,
      subProcesses: mode.subProcesses || []
    }));
  });

  return Object.keys(config).length > 0 ? config : null;
}

function extractAutomationConfig(planData) {
  return flowsToAutomationConfig(planData?.input?.executionPlan?.flows);
}

// Cycle-level automation config from executionPlanMap (the new per-cycle shape).
// Returns { [cycleName]: { induct, stow, diverter } } or null when absent.
function extractCycleAutomationConfig(planData) {
  const planMap = planData?.input?.executionPlanMap;
  if (!planMap || typeof planMap !== 'object') return null;

  const byCycle = {};
  Object.entries(planMap).forEach(([cycleName, executionPlan]) => {
    const config = flowsToAutomationConfig(executionPlan?.flows);
    if (config) byCycle[cycleName] = config;
  });

  return Object.keys(byCycle).length > 0 ? byCycle : null;
}

// Returns the automation config as-is from API data. No defaults injected —
// if a flow is absent from the plan, the UI simply won't render it.
function normalizeAutomationConfig(api) {
  if (!api) return null;
  return {
    ...(api.induct && { induct: api.induct }),
    ...(api.stow && { stow: api.stow }),
    ...(api.diverter && { diverter: api.diverter })
  };
}

function buildAutomationConfig(planData) {
  return normalizeAutomationConfig(planData ? extractAutomationConfig(planData) : null);
}

/**
 * Cycle-level automation config keyed by cycle when executionPlanMap is present;
 * otherwise null (caller falls back to the single flat config from executionPlan).
 */
function buildCycleAutomationConfig(planData, availableCycles) {
  const byCycle = planData ? extractCycleAutomationConfig(planData) : null;
  if (!byCycle) return null;

  const config = {};
  availableCycles.forEach((cycle) => {
    const normalized = normalizeAutomationConfig(byCycle[cycle]);
    if (normalized) config[cycle] = normalized;
  });
  // Guard the empty case: an executionPlanMap with no resolvable cycles would
  // otherwise yield {} → isCycleLevelAutomation=true → apply-back iterates zero
  // entries and skips the flat fallback, dropping automation overrides entirely.
  return Object.keys(config).length > 0 ? config : null;
}

function extractAdminSettings(planData) {
  const raw = planData?.input?.adminSettings;
  if (!raw) return null;
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch (e) {
    console.error('FlowPlannerModel — failed to parse adminSettings:', e);
    return null;
  }
}

const SUPPORT_PROCESS_GROUPS = new Set(['SITE_SUPPORT_GROUP', 'UTR_GROUP']);

function extractSupportProcesses(adminSettings) {
  if (!adminSettings?.laborProcesses) return [];
  return adminSettings.laborProcesses
    .filter((p) => SUPPORT_PROCESS_GROUPS.has(p.processGroup))
    .map((p) => p.name)
    .sort();
}

function extractSupportHeadcounts(planData, supportProcesses, availableCycles) {
  const shiftPlan = planData?.output?.shiftPlan;
  if (!Array.isArray(shiftPlan) || !supportProcesses.length) return {};
  const cycleMap = groupShiftPlanByCycle(shiftPlan);
  const result = {};
  const slotCount = Math.max(...Object.values(cycleMap).map((e) => e.length), 0);
  availableCycles.forEach((cycleName) => {
    const entries = cycleMap[cycleName] || [];
    result[cycleName] = supportProcesses.map((name) => ({
      name,
      headcounts: entries.length
        ? entries.map((e) => Math.round((e.requiredHeadcounts?.[name]?.headcount || 0) * 10) / 10)
        : Array(slotCount).fill(0)
    }));
  });
  return result;
}

function getDisplayName(key, fallback) {
  return DISPLAY_OVERRIDES[key] || fallback || key;
}

function getVolumeSource(processName, procMap, groupMap) {
  const proc = procMap[processName];
  const pg = proc?.processGroup;
  if (!pg) return 'INDUCT';
  if (VOLUME_SOURCE_OVERRIDES[pg]) return VOLUME_SOURCE_OVERRIDES[pg];
  return groupMap[pg]?.name?.toUpperCase() || 'INDUCT';
}

function createProcessEntry(processName, label) {
  const display = getDisplayName(processName, label || processName);
  return display === processName ? { name: display } : { name: display, rateName: processName };
}

// ─── Build PROCESS_HIERARCHY from admin.json ────────────────────────────────
// Produces the identical shape as the old hardcoded PROCESS_HIERARCHY:
//   [{ key, displayName, volumeSource, processes?, controllers?, automationSplit?, type? }]

function buildClaimedProcessSet(execFlowMap) {
  const claimed = new Set();
  GROUP_SKELETON.forEach((s) => {
    (s.processes || []).forEach((p) => claimed.add(p));
    if (s.fromExecutionFlow) {
      const flow = execFlowMap[s.fromExecutionFlow];
      if (flow) {
        flow.attributes.forEach((attr) => {
          if (attr.volumeAllocationProcess) claimed.add(attr.volumeAllocationProcess);
          (attr.subProcesses || []).forEach((sp) => claimed.add(sp));
        });
      }
    }
  });
  return claimed;
}

function buildControllerGroup(flow, flowKey, procMap, automationConfig) {
  const disabledTypes = new Set(
    (automationConfig?.[flowKey] || []).filter((m) => m.isEnabled === false).map((m) => m.executionType)
  );
  return flow.attributes
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    .filter((attr) => !disabledTypes.has(attr.name))
    .map((attr) => {
      const allProcesses = [attr.volumeAllocationProcess, ...(attr.subProcesses || [])];
      return {
        name: getDisplayName(attr.name, attr.name),
        executionType: attr.name,
        followers: allProcesses.map((pName, idx) => {
          const entry = createProcessEntry(pName, procMap[pName]?.label);
          if (idx === 0) entry.isVolumeProcess = true;
          return entry;
        })
      };
    });
}

function buildAutomationSplitEntry(
  flow,
  flowKey,
  mainProcessName,
  volumeSource,
  procMap,
  groupDisplayName,
  skeletonKey
) {
  const proc = createProcessEntry(mainProcessName, procMap[mainProcessName]?.label);
  const fclmName = procMap[mainProcessName]?.systemMappings?.fclm?.name;
  if (fclmName && fclmName !== proc.name && fclmName !== mainProcessName) {
    proc.displayName = fclmName;
  }
  const variants = flow.attributes
    .sort((a, b) => (a.order ?? 99) - (b.order ?? 99))
    .map((attr) => ({
      name: getDisplayName(
        `${mainProcessName}_${attr.name}`,
        procMap[attr.volumeAllocationProcess]?.label || attr.volumeAllocationProcess
      ),
      executionType: attr.name,
      rateName: attr.volumeAllocationProcess
    }));
  return {
    key: skeletonKey,
    displayName: groupDisplayName,
    volumeSource,
    processes: [proc],
    automationSplit: { automationGroup: flowKey, variants }
  };
}

function buildPhaseProcesses(skeleton, adminSettings, groupMap, procMap, claimed) {
  const excludeSet = skeleton.exclude ? new Set(skeleton.exclude) : null;
  return (adminSettings.laborProcesses || [])
    .filter((p) => {
      const grp = groupMap[p.processGroup];
      return (
        grp?.attributes?.allocationPhase === skeleton.fromPhase &&
        !claimed.has(p.name) &&
        (!excludeSet || !excludeSet.has(p.name))
      );
    })
    .map((p, idx) => {
      const entry = createProcessEntry(p.name, p.label);
      if (idx === 0) entry.isVolumeProcess = true;
      return entry;
    });
}

export function buildProcessHierarchy(adminSettings, automationConfig) {
  if (!adminSettings) return null;

  const procMap = {};
  (adminSettings.laborProcesses || []).forEach((p) => {
    procMap[p.name] = p;
  });

  const groupMap = {};
  (adminSettings.laborProcessGroups || []).forEach((g) => {
    groupMap[g.id] = g;
  });

  const execFlowMap = {};
  (adminSettings.executionFlows || []).forEach((f) => {
    execFlowMap[f.name] = f;
  });

  const hierarchy = [];
  const claimed = buildClaimedProcessSet(execFlowMap);

  GROUP_SKELETON.forEach((skeleton) => {
    if (skeleton.type === 'wip') {
      hierarchy.push({ key: skeleton.key, type: 'wip', displayName: 'Stow WIP' });
      return;
    }

    const groupDisplayName = getDisplayName(skeleton.key, groupMap[skeleton.key]?.name);

    if (skeleton.fromExecutionFlow) {
      const flow = execFlowMap[skeleton.fromExecutionFlow];
      if (!flow) return;
      const flowKey = FLOW_NAME_MAP[flow.name];
      const mainProcessName = (flow.attributes.find((a) => a.name === 'MECHANICAL') || flow.attributes?.[0])
        ?.volumeAllocationProcess;
      const volumeSource = getVolumeSource(mainProcessName, procMap, groupMap);

      if (flow.attributes?.length > 2) {
        const controllers = buildControllerGroup(flow, flowKey, procMap, automationConfig);
        hierarchy.push({ key: skeleton.key, displayName: groupDisplayName, volumeSource, controllers });
      } else {
        hierarchy.push(
          buildAutomationSplitEntry(
            flow,
            flowKey,
            mainProcessName,
            volumeSource,
            procMap,
            groupDisplayName,
            skeleton.key
          )
        );
      }
      return;
    }

    if (skeleton.fromPhase) {
      const phaseProcesses = buildPhaseProcesses(skeleton, adminSettings, groupMap, procMap, claimed);
      const volumeSource =
        phaseProcesses.length > 0
          ? getVolumeSource(phaseProcesses[0].rateName || phaseProcesses[0].name, procMap, groupMap)
          : 'PICK';
      hierarchy.push({ key: skeleton.key, displayName: groupDisplayName, volumeSource, processes: phaseProcesses });
      return;
    }

    const processes = (skeleton.processes || []).map((pName, idx) => {
      const entry = createProcessEntry(pName, procMap[pName]?.label);
      if (idx === 0) entry.isVolumeProcess = true;
      return entry;
    });
    const volumeSource =
      processes.length > 0 ? getVolumeSource(processes[0].rateName || processes[0].name, procMap, groupMap) : 'INDUCT';
    const group = { key: skeleton.key, displayName: groupDisplayName, volumeSource, processes };

    if (skeleton.sharesAutomation) {
      const sharedFlow = execFlowMap[skeleton.sharesAutomation];
      if (sharedFlow) {
        const flowKey = FLOW_NAME_MAP[sharedFlow.name];
        // Tag each process with its executionType; mark first per type as volume representative
        const seenTypes = new Set();
        processes.forEach((proc) => {
          const procName = proc.rateName || proc.name;
          for (const attr of sharedFlow.attributes) {
            if (attr.volumeAllocationProcess === procName || (attr.subProcesses || []).includes(procName)) {
              proc.executionType = attr.name;
              if (!seenTypes.has(attr.name)) {
                seenTypes.add(attr.name);
                proc.isVolumeProcess = true;
              }
              break;
            }
          }
        });
        group.automationGroup = flowKey;
      }
    }

    hierarchy.push(group);
  });

  return hierarchy;
}

// Extract available labor from SSPOT shift groups + VTO opportunities.
// Stores raw ISO strings — no Date parsing, no timezone dependency.
// ISO 8601 strings sort lexicographically in chronological order,
// so cross-midnight shifts ("2026-04-03T23:00" < "2026-04-04T02:40") just work.
function extractLaborAvailability(planData) {
  const la = planData?.input?.labourAvailability;
  if (!la?.shiftGroupAvailabilities?.length) return null;

  // Keep the full ISO string for sort/compare. Trim trailing seconds if present.
  // "2026-04-03T23:00:00" -> "2026-04-03T23:00"  (keep date for cross-midnight)
  const toISO = (raw) => {
    if (raw == null) return null;
    const s = String(raw);
    if (!s.includes('T')) return null;
    return s.substring(0, 16); // "YYYY-MM-DDTHH:MM"
  };

  const toHHMM = (iso) => (iso ? iso.substring(11, 16) : null);

  const shiftGroups = la.shiftGroupAvailabilities.map((g) => {
    const tw = g.applicability?.timeWindow || g.timeWindow || {};
    const startISO = toISO(tw.start);
    const endISO = toISO(tw.end);
    const hc = g.labourAvailabilityHeadCount || {};
    const rostered = hc.rosteredHeadcount || 0;
    const newHire = hc.day1NewHireHeadcount || 0;
    return {
      start: startISO,
      end: endISO,
      startLabel: toHHMM(startISO),
      endLabel: toHHMM(endISO),
      expectedHeadcount: hc.expectedHeadcount || 0,
      rosteredHeadcount: rostered,
      day1NewHireHeadcount: newHire,
      attendancePercentage: hc.attendancePercentage || 0,
      cohortLabel: (g.applicability?.identifier || '').split('#').pop() || `${toHHMM(startISO)}-${toHHMM(endISO)}`
    };
  });

  const vtoAccepted = (la.inputLabourOpportunities || [])
    .filter((o) => o.opportunityType === 'VTO' && o.accepted > 0)
    .map((o) => {
      const tw = o.applicability?.timeWindow || o.timeWindow || {};
      const startISO = toISO(tw.start);
      const endISO = toISO(tw.end);
      return {
        start: startISO,
        end: endISO,
        startLabel: toHHMM(startISO),
        endLabel: toHHMM(endISO),
        accepted: o.accepted
      };
    });

  return { shiftGroups, vtoAccepted, intervalAttendanceOverrides: la.intervalAttendanceOverrides || null };
}

export function getCycleTotalVolume(cycleData) {
  return (cycleData.plannedVolume || 0) + (cycleData.sidelineRTS || 0) - (cycleData.plannedRollover || 0);
}

export function computeAbsoluteVolumes(percentages, total) {
  let cumPct = 0;
  let prevCumVol = 0;
  return percentages.map((pct) => {
    cumPct += parseFloat(pct) || 0;
    const cumVol = Math.round((total * cumPct) / 100);
    const slotVol = cumVol - prevCumVol;
    prevCumVol = cumVol;
    return slotVol;
  });
}

// ─── Backend output: shiftPlan grid extractor ───────────────────────────────
// Reads output.shiftPlan[] → gridData[cycleName] = { tph, rows[], stowWIP[] }
// Row hierarchy mirrors processHierarchy (admin settings):
//   group → controller/process → leaf (follower)
// Leaf slots populated from requiredHeadcounts; parents aggregated from children.

// Build processName → volumeSource map from hierarchy for INDUCT / STOW classification.
// Used by both grid (stowWIP per-slot) and chart (cumulative curves).
// Only the main flow groups contribute — auxiliary groups (Water Spider, Problem Solve,
// Non-Con Stow, Divert) handle the same or adjacent items and would inflate totals.
const CUMULATIVE_CATEGORY = {
  INDUCT_GROUP: 'INDUCT',
  STOW: 'STOW',
  OUTBOUND: 'PICK'
};

function buildVolumeSourceMap(processHierarchy) {
  const vsMap = {};
  if (!processHierarchy) return vsMap;
  processHierarchy.forEach((g) => {
    const vs = CUMULATIVE_CATEGORY[g.key];
    if (!vs) return;
    if (g.controllers) {
      g.controllers.forEach((c) =>
        (c.followers || []).forEach((f) => {
          if (f.isVolumeProcess) vsMap[f.rateName || f.name] = vs;
        })
      );
    } else if (g.automationSplit) {
      g.automationSplit.variants.forEach((v) => {
        vsMap[v.rateName || v.name] = vs;
      });
    } else if (g.processes) {
      g.processes.forEach((p) => {
        if (p.isVolumeProcess) vsMap[p.rateName || p.name] = vs;
      });
    }
  });
  return vsMap;
}

function groupShiftPlanByCycle(shiftPlan) {
  const map = {};
  shiftPlan.forEach((e) => {
    if (!map[e.cycleName]) map[e.cycleName] = [];
    map[e.cycleName].push(e);
  });
  Object.values(map).forEach((a) =>
    a.sort((x, y) => (x.timeWindow?.start || '').localeCompare(y.timeWindow?.start || ''))
  );
  return map;
}

function buildCycleMetricsMap(shiftPlanMetrics) {
  const map = {};
  (shiftPlanMetrics?.cycleMetrics || []).forEach((cm) => {
    map[cm.cycleName] = cm;
  });
  return map;
}

function buildGridFromShiftPlan(
  planData,
  processHierarchy,
  automationConfig,
  vsMap,
  metricsMap,
  supportHeadcounts,
  indirectProcesses
) {
  const shiftPlan = planData?.output?.shiftPlan;
  if (!Array.isArray(shiftPlan) || shiftPlan.length === 0 || !processHierarchy) return null;

  const isDynamicV2 = planData.planVersion === 'DYNAMIC_V2';
  // Defined inline as it captures isDynamicV2 which varies per plan invocation
  const resolveKey = (key) => (isDynamicV2 && DYNAMIC_V2_KEY_OVERRIDES[key]) || key;
  // Reverse map: DYNAMIC_V2 key → STATIC_V3 key (e.g., Container Building → Stow)
  const reverseKeyOverrides = isDynamicV2
    ? Object.fromEntries(Object.entries(DYNAMIC_V2_KEY_OVERRIDES).map(([k, v]) => [v, k]))
    : {};

  const cycleMap = groupShiftPlanByCycle(shiftPlan);

  const gridData = {};
  Object.entries(cycleMap).forEach(([cycleName, entries]) => {
    const cycleMetric = metricsMap[cycleName];
    const processMetricsMap = {};
    (cycleMetric?.processMetrics || []).forEach((pm) => {
      processMetricsMap[pm.processName] = pm;
    });

    const numSlots = entries.length;

    const slotFor = (key, i) => {
      const resolvedKey = resolveKey(key);
      const data = entries[i]?.requiredHeadcounts?.[resolvedKey];
      const isIndirect = isIndirectProcess(resolvedKey, indirectProcesses);
      if (!data) {
        return {
          volume: isDynamicV2 && isIndirect ? null : 0,
          headcount: 0,
          workTime: 0,
          rate: isDynamicV2 && isIndirect ? null : 0
        };
      }
      const volume = Math.round(data.plannedFph || 0);
      const rate = Math.round(data.plannedPph || 0);
      const headcount = data.headcount || 0;
      return {
        volume: isDynamicV2 && isIndirect ? null : volume,
        headcount,
        workTime: data.durationInMins || 0,
        rate: isDynamicV2 && isIndirect ? null : rate
      };
    };

    const leaf = (displayName, key, isVolProc = true) => {
      const resolvedKey = resolveKey(key);

      let totalVolume = 0;
      const slots = [];
      for (let i = 0; i < numSlots; i++) {
        const slot = slotFor(key, i);
        slots.push(slot);
        totalVolume += slot.volume === null ? 0 : slot.volume;
      }
      const pm = processMetricsMap[resolvedKey] || processMetricsMap[key];
      // For DYNAMIC_V2, target rate may be under the original key (e.g., Stow instead of Container Building)
      const originalKey = reverseKeyOverrides[resolvedKey];
      const targetPm = originalKey ? processMetricsMap[originalKey] || pm : pm;
      const pvt = pm
        ? { planned: Math.round(pm.plannedRate), target: Math.round(targetPm?.targetRate || pm.targetRate) }
        : { planned: 0, target: 0 };
      const row = { key, displayName, totalVolume, pvt, slots };
      if (!isVolProc) row.isVolumeProcess = false;
      return row;
    };

    const aggregate = (children) => {
      let totalVolume = 0;
      const slots = [];
      for (let i = 0; i < numSlots; i++) {
        let volume = 0,
          headcount = 0,
          workTime = 0;
        children.forEach((child) => {
          if (child.isVolumeProcess !== false) volume += child.slots[i]?.volume || 0;
          headcount += child.slots[i]?.headcount || 0;
          workTime = Math.max(workTime, child.slots[i]?.workTime || 0);
        });
        slots.push({ volume, headcount, workTime, rate: 0 });
        totalVolume += volume;
      }
      return { totalVolume, slots };
    };

    const rows = [];
    processHierarchy.forEach((group) => {
      if (group.type === 'wip') return;

      if (group.controllers) {
        const ctrlRows = group.controllers.map((ctrl) => {
          const followerRows = (ctrl.followers || []).map((f) =>
            leaf(f.name, f.rateName || f.name, !!f.isVolumeProcess)
          );
          const aggregated = aggregate(followerRows);
          return {
            key: ctrl.executionType || ctrl.name,
            displayName: ctrl.name,
            totalVolume: aggregated.totalVolume,
            pvt: null,
            slots: aggregated.slots,
            children: followerRows
          };
        });
        const aggregated = aggregate(ctrlRows);
        rows.push({
          key: group.key,
          displayName: group.displayName,
          totalVolume: aggregated.totalVolume,
          pvt: null,
          slots: aggregated.slots,
          children: ctrlRows
        });
        return;
      }

      if (group.automationSplit) {
        const flowModes = automationConfig?.[group.automationSplit.automationGroup] || [];
        const variantRows = group.automationSplit.variants.map((v) => {
          const row = leaf(v.name, v.rateName || v.name);
          const mode = flowModes.find((m) => m.executionType === v.executionType);
          row.splitPercentage = mode?.volumeSplitPercentage ?? 50;
          return row;
        });
        const aggregated = aggregate(variantRows);
        rows.push({
          key: group.key,
          displayName: group.displayName,
          totalVolume: aggregated.totalVolume,
          pvt: null,
          slots: aggregated.slots,
          children: variantRows
        });
      } else if (group.processes) {
        const flowModes = automationConfig?.[group.automationGroup] || [];
        const procRows = (group.processes || []).map((p) => {
          const row = leaf(p.name, p.rateName || p.name, !!p.isVolumeProcess);
          if (p.executionType) {
            const mode = flowModes.find((m) => m.executionType === p.executionType);
            if (mode?.volumeSplitPercentage != null) row.splitPercentage = mode.volumeSplitPercentage;
          }
          return row;
        });
        const aggregated = aggregate(procRows);
        rows.push({
          key: group.key,
          displayName: group.displayName,
          totalVolume: aggregated.totalVolume,
          pvt: null,
          slots: aggregated.slots,
          children: procRows
        });
      }
    });

    const intervalMetrics = cycleMetric?.intervalMetrics || [];
    const stowWIP =
      intervalMetrics.length > 0
        ? Array.from({ length: numSlots }, (_, i) => Math.round(intervalMetrics[i]?.wipMinutes || 0))
        : Array(numSlots).fill(0);

    // Support headcount row (hidden from grid table, used by chart)
    const supportProcs = supportHeadcounts?.[cycleName] || [];
    if (supportProcs.length > 0) {
      const supportSlots = [];
      for (let i = 0; i < numSlots; i++) {
        supportSlots.push({
          volume: 0,
          headcount: supportProcs.reduce((s, p) => s + ((p.headcounts || [])[i] || 0), 0),
          workTime: 0,
          rate: 0
        });
      }
      rows.push({
        key: 'SUPPORT',
        displayName: 'Support',
        totalVolume: 0,
        pvt: null,
        slots: supportSlots,
        hidden: true
      });
    }

    gridData[cycleName] = {
      tph: cycleMetric?.tph ?? 0,
      rows,
      stowWIP
    };
  });

  return Object.keys(gridData).length > 0 ? gridData : null;
}

// ─── Backend output: cumulative volume / WIP chart ──────────────────────────
// Builds per-cycle { categories, cumulativeInduct, cumulativeStow, cumulativePick, stowWIP }
// from shiftPlan[].requiredHeadcounts, using processHierarchy.volumeSource to
// determine which processes contribute INDUCT vs STOW cumulative volume.

function buildVolumeWIPFromShiftPlan(planData, processHierarchy, intervals, vsMap, metricsMap) {
  const shiftPlan = planData?.output?.shiftPlan;
  if (!Array.isArray(shiftPlan) || shiftPlan.length === 0 || !processHierarchy || !intervals?.length) return null;

  const cycleMap = groupShiftPlanByCycle(shiftPlan);

  const result = {};
  Object.entries(cycleMap).forEach(([cycleName, entries]) => {
    const numSlots = Math.min(entries.length, intervals.length);
    const intervalMetrics = metricsMap[cycleName]?.intervalMetrics || [];
    let totalInduct = 0,
      totalStow = 0,
      totalPick = 0;
    const cumulativeInduct = [],
      cumulativeStow = [],
      cumulativePick = [],
      stowWIP = [];

    const firstStart = intervals[0]?.startLabel;
    if (firstStart) {
      cumulativeInduct.push({ x: firstStart, y: 0 });
      cumulativeStow.push({ x: firstStart, y: 0 });
      cumulativePick.push({ x: firstStart, y: 0 });
    }

    for (let i = 0; i < numSlots; i++) {
      let slotI = 0,
        slotS = 0,
        slotP = 0;
      Object.entries(entries[i].requiredHeadcounts || {}).forEach(([name, d]) => {
        const vol = d.plannedFph || 0;
        if (vsMap[name] === 'INDUCT') slotI += vol;
        else if (vsMap[name] === 'STOW') slotS += vol;
        else if (vsMap[name] === 'PICK') slotP += vol;
      });
      totalInduct += slotI;
      totalStow += slotS;
      totalPick += slotP;
      const endLbl = intervals[i].endLabel;
      cumulativeInduct.push({ x: endLbl, y: Math.round(totalInduct) });
      cumulativeStow.push({ x: endLbl, y: Math.round(totalStow) });
      cumulativePick.push({ x: endLbl, y: Math.round(totalPick) });
      stowWIP.push({ x: intervals[i].startLabel, y: Math.round(intervalMetrics[i]?.wipMinutes || 0) });
    }

    const seen = new Set();
    const categories = [];
    const addCat = (l) => {
      if (l && !seen.has(l)) {
        seen.add(l);
        categories.push(l);
      }
    };
    addCat(intervals[0]?.startLabel);
    intervals.forEach((iv) => {
      addCat(iv.startLabel);
      addCat(iv.endLabel);
    });

    result[cycleName] = { categories, cumulativeInduct, cumulativeStow, cumulativePick, stowWIP };
  });

  return Object.keys(result).length > 0 ? result : null;
}

function extractLaborSummaryFromShiftPlan(planData, intervals) {
  const shiftPlan = planData?.output?.shiftPlan;
  if (!Array.isArray(shiftPlan) || shiftPlan.length === 0) return null;

  const cycleMap = groupShiftPlanByCycle(shiftPlan);

  // Paid minutes per interval = interval duration - unpaid break overlap
  const paidMinsPerInterval = (intervals || []).map((iv) => {
    const intervalMins = (iv.endTime - iv.startTime) / 60;
    const unpaidMins = (iv.breaks || [])
      .filter((b) => b.breakType === 'UNPAID')
      .reduce(
        (s, b) => s + Math.max(0, (Math.min(b.endTime, iv.endTime) - Math.max(b.startTime, iv.startTime)) / 60),
        0
      );
    return intervalMins - unpaidMins;
  });

  let numSlots = 0;
  const cycles = [];
  Object.entries(cycleMap).forEach(([cycleName, entries]) => {
    numSlots = Math.max(numSlots, entries.length);
    const laborHours = entries.map((e) =>
      Object.values(e.requiredHeadcounts || {}).reduce(
        (s, p) => s + ((p.headcount || 0) * (p.paidTimeInMins || 0)) / 60,
        0
      )
    );
    // HC = laborHours * 60 / paidMinsPerInterval
    const absoluteHC = laborHours.map((lh, i) => (paidMinsPerInterval[i] > 0 ? (lh * 60) / paidMinsPerInterval[i] : 0));
    cycles.push({
      name: cycleName,
      absoluteHC,
      laborHours,
      absoluteTotal: absoluteHC.reduce((s, v) => s + v, 0),
      laborHoursTotal: laborHours.reduce((s, v) => s + v, 0)
    });
  });

  const grandHC = Array(numSlots).fill(0);
  const grandLH = Array(numSlots).fill(0);
  cycles.forEach((c) => {
    c.absoluteHC.forEach((v, i) => {
      grandHC[i] += v;
    });
    c.laborHours.forEach((v, i) => {
      grandLH[i] += v;
    });
  });

  return {
    cycles,
    grandTotals: {
      absoluteHC: grandHC,
      laborHours: grandLH,
      absoluteTotal: grandHC.reduce((s, v) => s + v, 0),
      laborHoursTotal: grandLH.reduce((s, v) => s + v, 0)
    }
  };
}

export function initializeModel(planResponseData) {
  // ─── Parse plan data first (needed for backend ops clock) ─────────────────
  let planData = null;
  if (planResponseData) {
    planData = typeof planResponseData === 'string' ? JSON.parse(planResponseData) : planResponseData;
  }

  // ─── Ops clock from backend input ─────────────────────────────────────────
  const opsClock = adaptOpsClockInput(planData);

  const availableCycles = getAvailableCycles(opsClock);
  const intervals = extractIntervalsFromShiftPlan(planData, opsClock?.breaks);

  const processEvents = {};
  availableCycles.forEach((cycleName) => {
    processEvents[cycleName] = {};
    FLOW_PROCESSES.forEach((process) => {
      processEvents[cycleName][process] = getCycleEvents(cycleName, process, opsClock);
    });
  });

  availableCycles.sort((a, b) => {
    const aStart = processEvents[a]?.PICK?.[0]?.timeIntervals?.[0]?.startTime ?? Infinity;
    const bStart = processEvents[b]?.PICK?.[0]?.timeIntervals?.[0]?.startTime ?? Infinity;
    return aStart - bStart;
  });

  let volumes = [];
  let rates = {};
  let laborAvailability = null;
  let processHierarchy = null;
  let supportProcesses = [];
  let indirectProcesses = new Set();
  const automationConfig = buildAutomationConfig(planData);
  // Cycle-level automation (new executionPlanMap shape); null for older plans
  // that only carry the single flat executionPlan.
  const cycleAutomationConfig = buildCycleAutomationConfig(planData, availableCycles);

  if (planData) {
    volumes = extractCycleVolumes(planData?.input?.packageProjectionInput?.packageProjectionList);
    availableCycles.forEach((cycleName) => {
      const cycleRates = extractProcessRates(planData, cycleName);
      if (cycleRates.length > 0) rates[cycleName] = cycleRates;
    });
    laborAvailability = extractLaborAvailability(planData);

    const adminSettings = extractAdminSettings(planData);
    processHierarchy = buildProcessHierarchy(adminSettings, automationConfig);
    supportProcesses = extractSupportProcesses(adminSettings);
    indirectProcesses = getProcessesByProcessingType(adminSettings, PROCESSING_TYPE.INDIRECT);
  }

  // ─── Volume distributions: from backend output only ───
  const outputVolDist = extractOutputVolumeDistribution(planData);

  const defaultVolDist = {};
  availableCycles.forEach((cycle) => {
    const cycleVol = volumes.find((v) => v.name === cycle);
    const total = cycleVol ? getCycleTotalVolume(cycleVol) : 0;
    defaultVolDist[cycle] = {};

    FLOW_PROCESSES.forEach((process) => {
      const processDist = outputVolDist?.[cycle]?.[process];
      const percentages = processDist?.percentages || [];
      const volumes = processDist?.volumes || [];
      defaultVolDist[cycle][process] = {
        percentages: [...percentages],
        volumes: [...volumes]
      };
    });
  });

  const defaultMultipliers = {};
  availableCycles.forEach((cycle) => {
    defaultMultipliers[cycle] = {};
    const twRates = extractTimeWindowRates(planData, cycle, intervals.length);

    if (twRates) {
      Object.entries(twRates).forEach(([processName, data]) => {
        defaultMultipliers[cycle][processName] = {
          percentages: data.multipliers,
          rates: data.rates
        };
      });
    } else {
      (rates[cycle] || []).forEach((p) => {
        const mults = Array(intervals.length).fill(DEFAULT_MULTIPLIER);
        defaultMultipliers[cycle][p.name] = {
          percentages: mults,
          rates: mults.map((m) => Math.round(p.targetRate * m))
        };
      });
    }
  });

  // ─── Outputs from backend shiftPlan ───────────────────────────────────────
  const vsMap = buildVolumeSourceMap(processHierarchy);

  // For DYNAMIC_V2, add override keys to vsMap so cumulative volume chart classifies them correctly
  if (planData?.planVersion === PLAN_VERSIONS.DYNAMIC_V2) {
    Object.entries(DYNAMIC_V2_KEY_OVERRIDES).forEach(([originalKey, dynamicKey]) => {
      if (vsMap[originalKey]) vsMap[dynamicKey] = vsMap[originalKey];
    });
  }
  const metricsMap = buildCycleMetricsMap(planData?.output?.shiftPlanMetrics);
  const supportHeadcounts = extractSupportHeadcounts(planData, supportProcesses, availableCycles);
  const gridData = buildGridFromShiftPlan(
    planData,
    processHierarchy,
    automationConfig,
    vsMap,
    metricsMap,
    supportHeadcounts,
    indirectProcesses
  );
  const laborSummary = extractLaborSummaryFromShiftPlan(planData, intervals);
  const laborAvailabilityChart = laborAvailability
    ? buildLaborAvailabilityChartData(laborAvailability, intervals)
    : null;
  const requiredHCPerInterval = laborSummary?.grandTotals?.absoluteHC || [];
  const availableHCPerInterval = computeAvailableHCPerInterval(laborAvailability, intervals);
  const volumeWIPChart = buildVolumeWIPFromShiftPlan(planData, processHierarchy, intervals, vsMap, metricsMap);
  const laborOpportunities = extractLaborOpportunities(planData);
  const opportunityHCPerInterval = computeOpportunityHCPerInterval(laborOpportunities, intervals);

  // ─── Default attendance overrides from backend ───
  const defaultAttendanceOverrides = {};
  if (laborAvailability?.intervalAttendanceOverrides) {
    const overrideList = laborAvailability.intervalAttendanceOverrides;
    if (Array.isArray(overrideList)) {
      overrideList.forEach((entry, idx) => {
        if (entry?.attendancePercentage != null) {
          defaultAttendanceOverrides[idx] = Math.round(entry.attendancePercentage * 100 * 10) / 10;
        }
      });
    }
  }

  return {
    rawInput: planData?.input || null,
    inputs: {
      availableCycles,
      processHierarchy,
      intervals,
      processRates: rates,
      processEvents,
      laborAvailability,
      supportHeadcounts,
      nodeZoneId: planData?.input?.nodeZoneId || null,
      opsClockEndTime: getOpsClockEndTime(planData)
    },
    overrides: {
      totalVolume: volumes,
      volumeDistributions: defaultVolDist,
      rateDistributions: defaultMultipliers,
      automationConfig,
      cycleAutomationConfig,
      isCycleLevelAutomation: !!cycleAutomationConfig,
      supportHeadcountOverrides: {},
      attendanceOverrides: defaultAttendanceOverrides
    },
    outputs: gridData
      ? {
          gridData,
          laborSummary,
          laborAvailabilityChart,
          requiredHCPerInterval,
          availableHCPerInterval,
          volumeWIPChart,
          laborOpportunities,
          opportunityHCPerInterval
        }
      : null
  };
}

/**
 * Merge user overrides into a deep copy of the backend rawInput to produce
 * the full ShiftPlanInput JSON for the CreateShiftPlan API — matching the
 * pattern used by the static pre-shift planner (sends entire input back).
 */
export function buildCreatePlanInput(rawInput, overrides) {
  if (!rawInput || !overrides) return null;
  const input = JSON.parse(JSON.stringify(rawInput));

  const pkgList = input.packageProjectionInput?.packageProjectionList;

  // 1. totalVolume → packageProjectionInput.packageProjectionList[].volume
  if (overrides.totalVolume && pkgList) {
    overrides.totalVolume.forEach((vol) => {
      const pkg = pkgList.find((p) => p.applicability?.identifier === vol.name);
      if (!pkg?.volume) return;
      pkg.volume.plannedVolume = vol.plannedVolume;
      pkg.volume.lastDayRolloverVolume = vol.sidelineRTS;
      pkg.volume.plannedRolloverVolume = vol.plannedRollover;
    });
  }

  // 2. volumeDistributions → packageProjectionInput.packageProjectionList[].volumeDistribution
  if (overrides.volumeDistributions && pkgList) {
    const ctwr = input.resourceProductivityInput?.cycleTimeWindowResourceProductivities;
    Object.entries(overrides.volumeDistributions).forEach(([cycleName, processMap]) => {
      const pkg = pkgList.find((p) => p.applicability?.identifier === cycleName);
      if (!pkg) return;
      const timeWindows = (ctwr?.[cycleName] || []).map((rp) => rp?.applicability?.timeWindow || null);
      const total = getCycleTotalVolume({
        plannedVolume: pkg.volume?.plannedVolume || 0,
        sidelineRTS: pkg.volume?.lastDayRolloverVolume || 0,
        plannedRollover: pkg.volume?.plannedRolloverVolume || 0
      });
      pkg.volumeDistribution = Object.entries(processMap).map(([processGroup, data]) => ({
        processGroup: processGroup.charAt(0) + processGroup.slice(1).toLowerCase(),
        intervals: computeAbsoluteVolumes(data.percentages || [], total).map((vol, i) => ({
          timeWindow: timeWindows[i] || null,
          volume: vol,
          distributionPercentage: parseFloat(data.percentages?.[i]) || 0
        }))
      }));
    });
  }

  // 3. rateDistributions
  if (overrides.rateDistributions) {
    const ctwr = input.resourceProductivityInput?.cycleTimeWindowResourceProductivities;
    const crp = input.resourceProductivityInput?.cycleResourceProductivities;

    // Cycle-level targetPph updates (independent of time windows)
    if (crp) {
      Object.entries(overrides.rateDistributions).forEach(([cycleName, processMap]) => {
        Object.entries(processMap).forEach(([displayName, data]) => {
          if (data.targetRate == null || !crp[cycleName]?.pph) return;
          const rawName = REVERSE_DISPLAY_OVERRIDES[displayName] || displayName;
          const pphKey = crp[cycleName].pph[rawName] ? rawName : displayName;
          if (crp[cycleName].pph[pphKey]) {
            crp[cycleName].pph[pphKey].targetPph = data.targetRate;
          }
        });
      });
    }

    // Time-window-level updates → cycleTimeWindowResourceProductivities
    if (ctwr) {
      Object.entries(overrides.rateDistributions).forEach(([cycleName, processMap]) => {
        const windows = ctwr[cycleName];
        if (!Array.isArray(windows)) return;
        Object.entries(processMap).forEach(([displayName, data]) => {
          const rawName = REVERSE_DISPLAY_OVERRIDES[displayName] || displayName;
          (data.percentages || []).forEach((multiplier, i) => {
            const pphKey = windows[i]?.pph?.[rawName] ? rawName : displayName;
            if (windows[i]?.pph?.[pphKey]) {
              windows[i].pph[pphKey].pphMultiplier = parseFloat(multiplier) || 1;
              windows[i].pph[pphKey].plannedPph = parseFloat(data.rates?.[i]) || 0;
            }
          });
        });
      });
    }
  }

  // 4. automationConfig → executionPlan(Map).flows[].executionModes
  // Applies the frontend { induct, stow, diverter } config onto a backend
  // ExecutionPlanBean (flows[]), mutating in place.
  const applyAutomationConfigToFlows = (flows, config) => {
    if (!Array.isArray(flows) || !config) return;
    Object.entries(config).forEach(([feKey, modes]) => {
      const backendName = REVERSE_FLOW_NAME_MAP[feKey];
      if (!backendName) return;
      const flow = flows.find((f) => f.name === backendName);
      if (flow && flow.executionModes) {
        flow.executionModes = flow.executionModes.map((backendMode) => {
          const override = modes.find((m) => m.executionType === backendMode.executionType);
          if (!override) return backendMode;
          return {
            ...backendMode,
            isEnabled: override.isEnabled,
            volumeSplitPercentage: override.volumeSplitPercentage,
            lineCount: override.lineCount,
            priority: override.priority
          };
        });
      }
    });
  };

  // Cycle-level automation: write each cycle's config back into executionPlanMap.
  // Older plans without executionPlanMap fall through to the flat executionPlan below.
  if (overrides.isCycleLevelAutomation && overrides.cycleAutomationConfig && input.executionPlanMap) {
    Object.entries(overrides.cycleAutomationConfig).forEach(([cycleName, config]) => {
      applyAutomationConfigToFlows(input.executionPlanMap[cycleName]?.flows, config);
    });
  } else if (overrides.automationConfig && input.executionPlan?.flows) {
    applyAutomationConfigToFlows(input.executionPlan.flows, overrides.automationConfig);
  }

  // 5. supportHeadcounts → shiftPlanOverride (all support processes)
  if (overrides.supportHeadcounts) {
    const ctwr = input.resourceProductivityInput?.cycleTimeWindowResourceProductivities;
    if (ctwr) {
      const userEdits = overrides.supportHeadcountOverrides || {};
      const overrideEntries = [];
      Object.entries(overrides.supportHeadcounts).forEach(([cycleName, processes]) => {
        const windows = ctwr[cycleName];
        if (!Array.isArray(windows) || !processes.length) return;
        const edits = userEdits[cycleName] || {};
        windows.forEach((tw, slotIndex) => {
          const hcMap = {};
          processes.forEach((p) => {
            const hc = edits[p.name]?.[slotIndex] ?? p.headcounts[slotIndex] ?? 0;
            hcMap[p.name] = {
              headcount: hc,
              plannedPph: 0,
              durationInMins: 0,
              requiredDurationInMins: 0,
              plannedFph: 0
            };
          });
          overrideEntries.push({
            cycleName,
            timeWindow: tw.applicability?.timeWindow || null,
            requiredHeadcounts: hcMap
          });
        });
      });
      if (overrideEntries.length > 0) {
        input.shiftPlanOverride = overrideEntries;
      }
    }
  }

  // 6. attendanceOverrides → labourAvailability.intervalAttendanceOverrides (full ordered list)
  if (overrides.attendanceOverrides && Object.keys(overrides.attendanceOverrides).length > 0) {
    const la = input.labourAvailability;
    const ctwr = input.resourceProductivityInput?.cycleTimeWindowResourceProductivities;
    if (la && ctwr) {
      const windows = Object.values(ctwr)[0] || [];
      la.intervalAttendanceOverrides = windows.map((rp, idx) => ({
        timeWindow: rp?.applicability?.timeWindow || null,
        attendancePercentage: (overrides.attendanceOverrides[idx] ?? DEFAULT_ATT_PCT) / 100
      }));
    }
  }

  return input;
}
